import { isProductImageUrl } from './product-presentation.js';
const blockTypes = new Set([
    'paragraph',
    'heading',
    'bulletList',
    'orderedList',
    'blockquote',
    'horizontalRule',
    'image',
]);
const fontFamilies = new Map([
    'Arial',
    'Helvetica',
    'sans-serif',
    'serif',
    'monospace',
    'Microsoft YaHei',
    'PingFang SC',
    'SimSun',
    'SimHei',
].map((font) => [font.toLowerCase(), font]));
function object(value, label) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        throw new Error(`${label}格式错误`);
    return value;
}
function allowedKeys(value, keys, label) {
    if (Object.keys(value).some((key) => !keys.includes(key)))
        throw new Error(`${label}包含不支持的属性`);
}
function optionalText(value, label, max) {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== 'string' || value.length > max)
        throw new Error(`${label}最多 ${max} 字`);
    return value.trim();
}
function color(value) {
    if (typeof value !== 'string')
        throw new Error('文字颜色格式无效');
    const normalized = value.trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/u.test(normalized))
        return normalized;
    if (/^#[0-9a-f]{3}$/u.test(normalized))
        return `#${[...normalized.slice(1)].map((letter) => `${letter}${letter}`).join('')}`;
    const rgb = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/u.exec(normalized);
    if (rgb) {
        const channels = rgb.slice(1).map(Number);
        if (channels.every((channel) => channel <= 255))
            return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
    }
    throw new Error('文字颜色须为有效的十六进制颜色');
}
function parseMarks(value) {
    if (value === undefined)
        return undefined;
    if (!Array.isArray(value) || value.length > 5)
        throw new Error('文字样式配置无效');
    const seen = new Set();
    return value.map((item) => {
        const mark = object(item, '文字样式');
        allowedKeys(mark, ['type', 'attrs'], '文字样式');
        if (typeof mark.type !== 'string' || seen.has(mark.type))
            throw new Error('文字样式重复或无效');
        seen.add(mark.type);
        if (['bold', 'italic', 'underline', 'strike'].includes(mark.type)) {
            if (mark.attrs !== undefined) {
                const attributes = object(mark.attrs, '文字样式');
                allowedKeys(attributes, [], '文字样式');
            }
            return { type: mark.type };
        }
        if (mark.type !== 'textStyle')
            throw new Error('存在不支持的文字样式');
        const attributes = object(mark.attrs ?? {}, '文字样式');
        allowedKeys(attributes, ['fontSize', 'color', 'backgroundColor', 'fontFamily'], '文字样式');
        const next = {};
        if (attributes.fontSize != null) {
            const size = typeof attributes.fontSize === 'number'
                ? attributes.fontSize
                : typeof attributes.fontSize === 'string' && /^\d{1,2}px$/u.test(attributes.fontSize)
                    ? Number(attributes.fontSize.slice(0, -2))
                    : NaN;
            if (!Number.isInteger(size) || size < 12 || size > 48)
                throw new Error('字号须为 12–48px 的整数');
            next.fontSize = `${size}px`;
        }
        if (attributes.color != null)
            next.color = color(attributes.color);
        if (attributes.backgroundColor != null)
            next.backgroundColor = color(attributes.backgroundColor);
        if (attributes.fontFamily != null) {
            if (typeof attributes.fontFamily !== 'string')
                throw new Error('请选择支持的字体');
            const font = fontFamilies.get(attributes.fontFamily
                .trim()
                .replace(/^['"]|['"]$/gu, '')
                .toLowerCase());
            if (!font)
                throw new Error('请选择支持的字体');
            next.fontFamily = font;
        }
        return { type: 'textStyle', attrs: next };
    });
}
function parseAttrs(type, value) {
    const attributes = object(value ?? {}, '富媒体内容属性');
    const allowed = type === 'paragraph' || type === 'heading'
        ? ['textAlign', 'lineHeight', ...(type === 'heading' ? ['level'] : [])]
        : type === 'orderedList'
            ? ['start', 'type']
            : type === 'image'
                ? ['src', 'alt', 'title', 'width', 'align', 'textAlign']
                : [];
    allowedKeys(attributes, allowed, '富媒体内容');
    const next = {};
    if (type === 'heading') {
        if (![1, 2, 3].includes(attributes.level))
            throw new Error('标题级别须为 1–3');
        next.level = attributes.level;
    }
    if (attributes.textAlign != null && type !== 'image') {
        if (!['left', 'center', 'right', 'justify'].includes(String(attributes.textAlign)))
            throw new Error('文字对齐方式无效');
        next.textAlign = attributes.textAlign;
    }
    if (attributes.lineHeight != null) {
        const lineHeight = String(attributes.lineHeight);
        if (!['1.2', '1.5', '1.8', '2', '2.2'].includes(lineHeight))
            throw new Error('行距设置无效');
        next.lineHeight = lineHeight;
    }
    if (type === 'orderedList' && attributes.start != null) {
        if (!Number.isInteger(attributes.start) ||
            Number(attributes.start) < 1 ||
            Number(attributes.start) > 999)
            throw new Error('编号须为 1–999 的整数');
        next.start = attributes.start;
    }
    if (type === 'orderedList' && attributes.type != null) {
        if (!['1', 'a', 'A', 'i', 'I'].includes(String(attributes.type)))
            throw new Error('列表编号样式无效');
        next.type = attributes.type;
    }
    if (type === 'image') {
        if (!isProductImageUrl(attributes.src))
            throw new Error('请选择已上传图片或有效的 HTTPS 图片');
        next.src = attributes.src;
        const alt = optionalText(attributes.alt, '图片说明', 200);
        const title = optionalText(attributes.title, '图片标题', 200);
        if (alt !== undefined)
            next.alt = alt;
        if (title !== undefined)
            next.title = title;
        if (attributes.width != null) {
            if (!['25%', '50%', '75%', '100%'].includes(String(attributes.width)))
                throw new Error('图片宽度须为 25%、50%、75% 或 100%');
            next.width = attributes.width;
        }
        const imageAlign = attributes.textAlign ?? attributes.align;
        if (imageAlign != null) {
            if (!['left', 'center', 'right'].includes(String(imageAlign)))
                throw new Error('图片对齐方式无效');
            next.textAlign = imageAlign;
        }
    }
    return Object.keys(next).length ? next : undefined;
}
/** Validate every node, attribute and mark at both write and rendering boundaries. */
export function parseProductRichText(value) {
    let count = 0;
    let textLength = 0;
    const visit = (input, parent, depth) => {
        if (depth > 12)
            throw new Error('富媒体内容嵌套最多 12 层');
        if (++count > 1200)
            throw new Error('富媒体内容最多 1200 个节点');
        const node = object(input, '富媒体内容');
        allowedKeys(node, ['type', 'attrs', 'content', 'text', 'marks'], '富媒体内容');
        const type = node.type;
        if (typeof type !== 'string')
            throw new Error('富媒体内容类型无效');
        const childAllowed = parent === null
            ? type === 'doc'
            : parent === 'paragraph' || parent === 'heading'
                ? type === 'text' || type === 'hardBreak'
                : parent === 'bulletList' || parent === 'orderedList'
                    ? type === 'listItem'
                    : parent === 'doc' || parent === 'blockquote' || parent === 'listItem'
                        ? blockTypes.has(type)
                        : false;
        if (!childAllowed)
            throw new Error('富媒体内容类型或排版结构不支持');
        const next = { type: type };
        const attributes = parseAttrs(type, node.attrs);
        if (attributes)
            next.attrs = attributes;
        if (type === 'text') {
            if (typeof node.text !== 'string' || node.text.length === 0)
                throw new Error('文字内容格式错误');
            textLength += node.text.length;
            next.text = node.text;
        }
        else if (node.text !== undefined)
            throw new Error('仅文字节点允许填写文字');
        if (textLength > 20000)
            throw new Error('富媒体文字最多 20000 字');
        if (node.marks !== undefined) {
            if (type !== 'text' && type !== 'hardBreak')
                throw new Error('仅文字允许设置文字样式');
            next.marks = parseMarks(node.marks);
        }
        if (['text', 'hardBreak', 'horizontalRule', 'image'].includes(type)) {
            if (node.content !== undefined)
                throw new Error('当前内容节点不允许包含子内容');
        }
        else {
            if (node.content !== undefined && !Array.isArray(node.content))
                throw new Error('富媒体子内容格式错误');
            const children = (node.content ?? []);
            if ((type === 'bulletList' || type === 'orderedList' || type === 'listItem') &&
                !children.length)
                throw new Error('请填写列表内容或移除空白列表');
            next.content = children.map((child) => visit(child, type, depth + 1));
            if (type === 'listItem' && next.content[0]?.type !== 'paragraph')
                throw new Error('列表须以文字段落开始');
        }
        return next;
    };
    const result = visit(value, null, 1);
    const plainText = productRichTextPlainText(result);
    if (!plainText)
        throw new Error('请填写介绍内容或删除空白内容块');
    if (plainText.length > 20000)
        throw new Error('富媒体文字最多 20000 字');
    return result;
}
/** Plain text remains available for old clients, search and accessibility. */
export function productRichTextPlainText(document) {
    const read = (node) => {
        if (node.type === 'text')
            return node.text ?? '';
        if (node.type === 'hardBreak')
            return '\n';
        if (node.type === 'image')
            return node.attrs?.alt || node.attrs?.title || '线路图片';
        if (node.type === 'horizontalRule')
            return '';
        const separator = node.type === 'paragraph' || node.type === 'heading' ? '' : '\n';
        return (node.content ?? []).map(read).join(separator);
    };
    return read(document).trim();
}
function renderedElement(name, children = [], attrs = {}) {
    return { type: 'node', name, attrs, children };
}
/** Render a validated document into WeChat RichText nodes without interpreting HTML. */
export function productRichTextToNodes(document, resolveImageUrl) {
    const parsed = parseProductRichText(document);
    const render = (node) => {
        if (node.type === 'text' || node.type === 'hardBreak') {
            let result = node.type === 'text' ? { type: 'text', text: node.text } : renderedElement('br');
            for (const mark of node.marks ?? []) {
                if (mark.type !== 'textStyle') {
                    const names = { bold: 'strong', italic: 'em', underline: 'u', strike: 's' };
                    result = renderedElement(names[mark.type], [result]);
                }
                else {
                    const style = Object.entries(mark.attrs)
                        .map(([key, value]) => {
                        const property = {
                            fontSize: 'font-size',
                            color: 'color',
                            backgroundColor: 'background-color',
                            fontFamily: 'font-family',
                        }[key];
                        return `${property}:${key === 'fontFamily' && value.includes(' ') ? `'${value}'` : value}`;
                    })
                        .join(';');
                    if (style)
                        result = renderedElement('span', [result], { style });
                }
            }
            return [result];
        }
        if (node.type === 'image') {
            const source = node.attrs.src;
            const resolved = resolveImageUrl ? resolveImageUrl(source) : source;
            if (!isProductImageUrl(resolved))
                return [];
            const attrs = node.attrs;
            const align = attrs.textAlign ?? 'left';
            const margins = align === 'center'
                ? 'margin-left:auto;margin-right:auto'
                : align === 'right'
                    ? 'margin-left:auto;margin-right:0'
                    : 'margin-left:0;margin-right:auto';
            return [
                renderedElement('img', [], {
                    src: resolved,
                    alt: attrs.alt ?? '',
                    ...(attrs.title ? { title: attrs.title } : {}),
                    style: `display:block;max-width:100%;height:auto;width:${attrs.width ?? '100%'};${margins}`,
                }),
            ];
        }
        const children = (node.content ?? []).flatMap(render);
        if (node.type === 'doc')
            return children;
        const names = {
            paragraph: 'p',
            heading: `h${node.attrs?.level ?? 2}`,
            bulletList: 'ul',
            orderedList: 'ol',
            listItem: 'li',
            blockquote: 'blockquote',
            horizontalRule: 'hr',
        };
        const styles = [];
        if (node.attrs?.textAlign)
            styles.push(`text-align:${node.attrs.textAlign}`);
        if (node.attrs?.lineHeight)
            styles.push(`line-height:${node.attrs.lineHeight}`);
        const attrs = {};
        if (styles.length)
            attrs.style = styles.join(';');
        if (node.type === 'orderedList' && node.attrs?.start !== undefined)
            attrs.start = String(node.attrs.start);
        if (node.type === 'orderedList' && node.attrs?.type !== undefined)
            attrs.type = node.attrs.type;
        return [renderedElement(names[node.type], children, attrs)];
    };
    return render(parsed);
}
//# sourceMappingURL=product-rich-text.js.map